/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Helpers for the coach dashboard's compact client list: display name, an "attention" descriptor
 * (used both for the per-row badge and the "needs attention" sort), and a pure filter+sort function.
 *
 * Kept framework-free so the ranking/triage logic can be unit tested without rendering React.
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** Default number of days of inactivity before an active client is flagged as needing attention. */
export const DEFAULT_INACTIVE_DAYS = 14

/** Human display name for a client, falling back through publicName / email. */
export function getClientDisplayName(client) {
  if (client?.firstName || client?.lastName) {
    return `${client.firstName || ''} ${client.lastName || ''}`.trim()
  }
  return client?.publicName || client?.email || 'Unknown User'
}

/** Epoch ms of the client's last activity, or 0 when they have never been active. */
export function lastActivityTime(client) {
  const raw = client?.stats?.lastActivity
  if (!raw) return 0
  const t = new Date(raw).getTime()
  return Number.isNaN(t) ? 0 : t
}

/**
 * Classify a client into an attention tier for triage. Lower tier = more urgent:
 *   0  coach must sign the tutoring agreement before tutoring can begin
 *   1  other non-active relationship (awaiting guardian approval / agreements / acceptance)
 *   2  active but stale: no activity ever, or none in the last `inactiveDays` days
 *   3  active and recently engaged — nothing to flag
 *
 * Returns { tier, label } where `label` is the inactivity text for tier 2 (e.g. "Inactive 21d" or
 * "No activity yet") and null otherwise (tiers 0/1 render the relationship status badge instead).
 */
export function getClientAttention(
  client,
  { inactiveDays = DEFAULT_INACTIVE_DAYS, now = Date.now() } = {}
) {
  const status = client?.relationshipStatus || 'active'
  const coachSigned = !!client?.signedRoles?.includes('coach')

  if (status === 'pending_agreements' && !coachSigned) {
    return { tier: 0, label: null }
  }
  if (status !== 'active') {
    return { tier: 1, label: null }
  }

  const last = lastActivityTime(client)
  if (last === 0) {
    return { tier: 2, label: 'No activity yet' }
  }
  const days = Math.floor((now - last) / DAY_MS)
  if (days >= inactiveDays) {
    return { tier: 2, label: `Inactive ${days}d` }
  }
  return { tier: 3, label: null }
}

/** True when the client's name or email contains the (already lower-cased) search term. */
function matchesSearch(client, term) {
  const name = getClientDisplayName(client).toLowerCase()
  const email = (client?.email || '').toLowerCase()
  return name.includes(term) || email.includes(term)
}

/** The valid sort keys for the client list. */
export const CLIENT_SORTS = ['attention', 'recent', 'name', 'score']

/**
 * Filter clients by a free-text query (name or email) and sort by the chosen key. Pure: it returns a
 * new array and never mutates the input.
 *
 * @param {Array} clients
 * @param {object} opts
 * @param {string} [opts.search]      case-insensitive substring matched against name + email
 * @param {string} [opts.sort]        one of CLIENT_SORTS (defaults to 'attention')
 * @param {number} [opts.now]         injectable clock for deterministic tests
 * @param {number} [opts.inactiveDays]
 */
export function filterAndSortClients(
  clients,
  { search = '', sort = 'attention', now = Date.now(), inactiveDays = DEFAULT_INACTIVE_DAYS } = {}
) {
  const list = Array.isArray(clients) ? clients.slice() : []

  const term = search.trim().toLowerCase()
  const filtered = term ? list.filter((c) => matchesSearch(c, term)) : list

  const byName = (a, b) => getClientDisplayName(a).localeCompare(getClientDisplayName(b))
  const score = (c) => (typeof c?.stats?.averageScore === 'number' ? c.stats.averageScore : null)

  const comparators = {
    name: byName,
    recent: (a, b) => lastActivityTime(b) - lastActivityTime(a) || byName(a, b),
    score: (a, b) => {
      const sa = score(a)
      const sb = score(b)
      if (sa === sb) return byName(a, b)
      if (sa === null) return 1 // nulls last
      if (sb === null) return -1
      return sb - sa
    },
    attention: (a, b) => {
      const ta = getClientAttention(a, { inactiveDays, now }).tier
      const tb = getClientAttention(b, { inactiveDays, now }).tier
      if (ta !== tb) return ta - tb
      // Within the stale tier, surface the most-stale (oldest activity) first; elsewhere the most
      // recently active first. Never-active clients (time 0) thus lead the stale tier.
      const la = lastActivityTime(a)
      const lb = lastActivityTime(b)
      if (ta === 2) return la - lb || byName(a, b)
      return lb - la || byName(a, b)
    }
  }

  const cmp = comparators[sort] || comparators.attention
  return filtered.sort(cmp)
}
